import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Box,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Holiday, Term } from '../../types';

interface CalendarDay {
  date: Date;
  dateStr: string;
  dayOfMonth: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
  isTermTime: boolean;
  isHoliday: boolean;
  isInset: boolean;
  term?: Term;
  holiday?: Holiday;
}

interface CalendarViewProps {
  terms: Term[];
  holidays: Holiday[];
  academicYear: string;
  onDateClick?: (date: Date) => void;
}

export const CalendarView: React.FC<CalendarViewProps> = ({ terms, holidays, academicYear, onDateClick }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getTermForDate = (dateStr: string): Term | undefined => {
    return terms.find((term) => dateStr >= term.startDate && dateStr <= term.endDate);
  };

  const getHolidayForDate = (dateStr: string): Holiday | undefined => {
    return holidays.find((holiday) => dateStr >= holiday.startDate && dateStr <= holiday.endDate);
  };

  const generateCalendarDays = (): CalendarDay[] => {
    const days: CalendarDay[] = [];
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];

    // Add days from previous month to fill the first week
    const firstDayOfWeek = firstDay.getDay();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(currentYear, currentMonth, -i);
      const dateStr = date.toISOString().split('T')[0];
      const term = getTermForDate(dateStr);
      const holiday = getHolidayForDate(dateStr);

      days.push({
        date,
        dateStr,
        dayOfMonth: date.getDate(),
        isCurrentMonth: false,
        isToday: dateStr === todayStr,
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isTermTime: !!term,
        isHoliday: !!holiday && holiday.type !== 'inset',
        isInset: !!holiday && holiday.type === 'inset',
        term,
        holiday,
      });
    }

    // Add days of current month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(currentYear, currentMonth, day);
      const dateStr = date.toISOString().split('T')[0];
      const term = getTermForDate(dateStr);
      const holiday = getHolidayForDate(dateStr);

      days.push({
        date,
        dateStr,
        dayOfMonth: day,
        isCurrentMonth: true,
        isToday: dateStr === todayStr,
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isTermTime: !!term,
        isHoliday: !!holiday && holiday.type !== 'inset',
        isInset: !!holiday && holiday.type === 'inset',
        term,
        holiday,
      });
    }

    // Add days from next month to fill the last week
    const remainingDays = 42 - days.length; // 6 weeks * 7 days
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(currentYear, currentMonth + 1, day);
      const dateStr = date.toISOString().split('T')[0];
      const term = getTermForDate(dateStr);
      const holiday = getHolidayForDate(dateStr);

      days.push({
        date,
        dateStr,
        dayOfMonth: day,
        isCurrentMonth: false,
        isToday: dateStr === todayStr,
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isTermTime: !!term,
        isHoliday: !!holiday && holiday.type !== 'inset',
        isInset: !!holiday && holiday.type === 'inset',
        term,
        holiday,
      });
    }

    return days;
  };

  const handlePreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleMonthChange = (month: number) => {
    setCurrentMonth(month);
  };

  const getDayBackgroundColor = (day: CalendarDay): string => {
    if (!day.isCurrentMonth) return '#fafafa';
    if (day.isToday) return '#1976d2';
    if (day.isInset) return '#fff3e0';
    if (day.isHoliday) return '#f3e5f5';
    if (day.isTermTime) return '#e8f5e9';
    if (day.isWeekend) return '#f5f5f5';
    return 'white';
  };

  const getDayTextColor = (day: CalendarDay): string => {
    if (!day.isCurrentMonth) return '#bdbdbd';
    if (day.isToday) return 'white';
    return 'inherit';
  };

  const calendarDays = generateCalendarDays();

  return (
    <Paper sx={{ p: 3 }}>
      {/* Header with month navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handlePreviousMonth}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6">
            {monthNames[currentMonth]} {currentYear}
          </Typography>
          <IconButton onClick={handleNextMonth}>
            <ChevronRightIcon />
          </IconButton>
        </Box>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Month</InputLabel>
          <Select value={currentMonth} label="Month" onChange={(e) => handleMonthChange(e.target.value as number)}>
            {monthNames.map((month, index) => (
              <MenuItem key={month} value={index}>
                {month}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Chip label="Term Time" size="small" sx={{ backgroundColor: '#e8f5e9' }} />
        <Chip label="Holiday" size="small" sx={{ backgroundColor: '#f3e5f5' }} />
        <Chip label="INSET Day" size="small" sx={{ backgroundColor: '#fff3e0' }} />
        <Chip label="Weekend" size="small" sx={{ backgroundColor: '#f5f5f5' }} />
        <Chip label="Today" size="small" sx={{ backgroundColor: '#1976d2', color: 'white' }} />
      </Box>

      {/* Calendar grid */}
      <Grid container spacing={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        {/* Day names header */}
        {dayNames.map((dayName) => (
          <Grid
            item
            xs={12 / 7}
            key={dayName}
            sx={{
              p: 1,
              textAlign: 'center',
              backgroundColor: '#f5f5f5',
              borderBottom: '1px solid',
              borderColor: 'divider',
              fontWeight: 'bold',
            }}
          >
            <Typography variant="caption">{dayName}</Typography>
          </Grid>
        ))}

        {/* Calendar days */}
        {calendarDays.map((day, index) => (
          <Grid
            item
            xs={12 / 7}
            key={index}
            onClick={() => day.isCurrentMonth && onDateClick?.(day.date)}
            sx={{
              p: 1,
              minHeight: 80,
              backgroundColor: getDayBackgroundColor(day),
              color: getDayTextColor(day),
              border: '1px solid',
              borderColor: 'divider',
              cursor: day.isCurrentMonth ? 'pointer' : 'default',
              '&:hover': day.isCurrentMonth
                ? {
                    backgroundColor: day.isToday ? '#1565c0' : '#e3f2fd',
                  }
                : {},
            }}
          >
            <Tooltip
              title={
                <Box>
                  {day.term && <Typography variant="caption">{day.term.name}</Typography>}
                  {day.holiday && (
                    <Typography variant="caption" display="block">
                      {day.holiday.name}
                    </Typography>
                  )}
                </Box>
              }
              arrow
            >
              <Box>
                <Typography variant="body2" fontWeight={day.isToday ? 'bold' : 'normal'} sx={{ mb: 0.5 }}>
                  {day.dayOfMonth}
                </Typography>
                {day.holiday && (
                  <Chip
                    label={day.isInset ? 'INSET' : 'Holiday'}
                    size="small"
                    sx={{ fontSize: '0.6rem', height: 16 }}
                  />
                )}
              </Box>
            </Tooltip>
          </Grid>
        ))}
      </Grid>

      {/* Academic year info */}
      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary">
          Academic Year: {academicYear}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Total Terms: {terms.length}
        </Typography>
      </Box>
    </Paper>
  );
};

export default CalendarView;
